import type { SxProps, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'
import { densityPx } from '@/theme/base/density'

/** Espacement vertical symétrique entre le divider de page et le contenu (cartes / tableau). */
export const SETTINGS_TOOLBAR_SECTION_SPACING = 2.5

export const settingsToolbarSectionSx: SxProps<Theme> = {
  py: SETTINGS_TOOLBAR_SECTION_SPACING,
}

/** Padding sous le fil d'Ariane / chip, avant le divider de page. */
export const SETTINGS_PAGE_HEADER_PADDING_BOTTOM = 2.5

export const settingsPageHeaderSx: SxProps<Theme> = (theme) => ({
  pb: SETTINGS_PAGE_HEADER_PADDING_BOTTOM,
  borderBottom: `1px solid ${theme.palette.divider}`,
})

export const SETTINGS_PAGE_TITLE_INFO_ICON_PX = densityPx(18)

export const settingsPageHeaderTitleRowSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  flexWrap: 'wrap',
}

export const settingsPageHeaderTitleSx: SxProps<Theme> = {
  fontSize: 20,
  fontWeight: 600,
  letterSpacing: '-0.2px',
  color: 'text.primary',
}

const settingsPageHeaderSubtitleBase = (theme: Theme) => ({
  fontSize: 12,
  mt: 0.75,
  color: theme.palette.neutral?.[500] ?? theme.palette.text.secondary,
})

export const settingsPageHeaderSubtitleSx: SxProps<Theme> =
  settingsPageHeaderSubtitleBase

export const settingsPageHeaderSubtitleWithMaxWidth = (
  maxWidth: number
): SxProps<Theme> => (theme) => ({
  ...settingsPageHeaderSubtitleBase(theme),
  maxWidth,
})

export const settingsPageHeaderChipSx: SxProps<Theme> = (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1.25,
  flexWrap: 'wrap',
  rowGap: 1,
  mt: 1.75,
  py: '7px',
  px: '14px',
  maxWidth: '100%',
  borderRadius: '10px',
  border: `1px solid ${theme.palette.divider}`,
  bgcolor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.common.white,
})

/** Espacement grille liste Agences / Utilisateurs. */
export const SETTINGS_LIST_GRID_SPACING = 1.75

/** Hauteur commune des contrôles de barre d'outils (recherche, filtres, modes d'affichage). */
export const SETTINGS_TOOLBAR_CONTROL_HEIGHT = densityPx(36)

export const SETTINGS_TOOLBAR_SEARCH_ICON_PX = densityPx(15)

export const settingsToolbarRowSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1.5,
  width: '100%',
  minHeight: SETTINGS_TOOLBAR_CONTROL_HEIGHT,
}

export const settingsToolbarFormControlSx: SxProps<Theme> = {
  m: 0,
  '& .MuiOutlinedInput-root': {
    height: SETTINGS_TOOLBAR_CONTROL_HEIGHT,
    boxSizing: 'border-box',
    alignItems: 'center',
  },
}

export const settingsToolbarOutlinedInputSx: SxProps<Theme> = {
  boxSizing: 'border-box',
  height: SETTINGS_TOOLBAR_CONTROL_HEIGHT,
  alignItems: 'center',
  '& .MuiOutlinedInput-input': {
    py: 0,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
}

export const settingsToolbarSelectSx: SxProps<Theme> = {
  fontSize: densityPx(12),
  borderRadius: '8px',
  bgcolor: 'background.paper',
  boxSizing: 'border-box',
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    py: 0,
    minHeight: 'unset',
    boxSizing: 'border-box',
  },
}

export function settingsToolbarToggleButtonGroupSx(
  theme: Theme,
  subtle: string
): SxProps<Theme> {
  return {
    '& .MuiToggleButton-root': {
      px: 1,
      py: 0,
      minWidth: SETTINGS_TOOLBAR_CONTROL_HEIGHT,
      height: SETTINGS_TOOLBAR_CONTROL_HEIGHT,
      boxSizing: 'border-box',
      borderColor: theme.palette.divider,
      color: subtle,
      '&.Mui-selected': {
        color: theme.palette.primary.main,
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderColor: alpha(theme.palette.primary.main, 0.35),
      },
    },
  }
}
